import { Button, cn, InfoPopover, Stack } from 'component-library';
import { FC, HTMLAttributes } from 'react';
import { Link } from 'react-router-dom';

import { useIsConservation } from '@/hooks/useIsConservation';
import { isReactNode } from '@/utils/isReactNode';

import { ChartLegendBarItem } from './types';

type ChartLegendBarProps = HTMLAttributes<HTMLDivElement> & {
  items: ChartLegendBarItem[];
};

export const ChartLegendBar: FC<ChartLegendBarProps> = ({ items, className, ...delegated }) => {
  const isConservation = useIsConservation();

  const styles = cn('flex flex-col flex-wrap sm:flex-row items-center self-center sm:self-start gap-2', className);

  return (
    <div className={styles} {...delegated}>
      {items.map(({ label, color, shape = 'area', popoverContent, to, className: itemClassName, ...props }, i) => {
        const legendItemContent = (
          <div
            className={cn(
              'flex items-center gap-2',
              isConservation ? 'px-2 py-1' : 'bg-neutral-black-4 rounded-lg p-2',
              itemClassName,
            )}
            {...props}
          >
            {isReactNode(shape) && typeof shape !== 'string' ? (
              shape
            ) : (
              <div
                className={cn({
                  'h-2 w-2 rounded-full': shape === 'area',
                  'h-0.5 w-2 rounded': shape === 'line',
                  'h-3 w-2': shape === 'column',
                })}
                style={{ backgroundColor: color }}
              />
            )}
            <span
              className={cn(
                'text-text-secondary typography-caption',
                to && 'typography-body1 text-primary-100 underline underline-offset-4',
              )}
            >
              {label}
            </span>
            {popoverContent && <InfoPopover title={popoverContent.title} body={popoverContent.body} />}
          </div>
        );

        return to ? (
          <Link
            to={to}
            key={`${label}-${i}`}
            className={cn('flex flex-row items-center gap-1 rounded-md', {
              'border border-divider': isConservation,
            })}
          >
            {legendItemContent}
          </Link>
        ) : (
          <div
            key={`${label}-${i}`}
            className={cn('flex flex-row items-center gap-1 rounded-md', {
              'border border-divider': isConservation,
            })}
          >
            {legendItemContent}
          </div>
        );
      })}
    </div>
  );
};

export type LegendItemProps = {
  label: string;
  color: string | null;
  isActive: boolean;
  onClick: () => void;
};

export const LegendButton = ({ label, color, isActive, onClick }: LegendItemProps) => {
  return (
    <Button
      variant='outline'
      size='small'
      className={cn('p-0', {
        'bg-neutral-hover': isActive === true,
        'border-text-secondary': isActive === false,
      })}
      onClick={onClick}
    >
      <Stack direction='row' spacing={2} centerMain className='rounded-md px-4 py-1 items-center'>
        {color && <div className='h-3 w-2' style={{ backgroundColor: color }} />}
        <span className='text-text-secondary typography-body1'>{label}</span>
      </Stack>
    </Button>
  );
};
